import { useEffect, useRef, useState } from 'react';

import { Event, EventOutput, EventsFilter, EventUpdate, Fleet } from '../interfaces';
import { setExpirationDateForGroups } from '../queries';
import { EventsService } from '../services';
import { deepCopy, hash } from '../utils';
import { useEventTypes } from './useEventTypes';
import { useFleet } from './useFleet';
import { useHasAccess } from './useHasAccess';
import { useHasPermission } from './useHasPermission';

interface Props {
  filter: EventsFilter;
}

export const useGenericEventsQueue = (
  props: Props,
  eventGetterFn: (filter: EventsFilter, fleet: Fleet, abortSignal?: AbortSignal) => Promise<EventOutput>,
) => {
  const { filter } = props;
  const { skipped_event_ids, ...filterWithoutCursor } = filter;
  const { group_events } = filter;

  const [events, setEvents] = useState<Event[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [index, setIndex] = useState<number>(0);
  const fleet = useFleet();
  const eventTypes = useEventTypes();

  const current = events[index];
  const next = events[index + 1] || null;
  const prev = events[index - 1] || null;

  const abortController = useRef(new AbortController());
  let abortSignal = abortController.current.signal;

  const abortExistingRequest = () => {
    abortController.current.abort();

    abortController.current = new AbortController();
    abortSignal = abortController.current.signal;
  };

  const resetQueue = async () => {
    setLoading(true);
    setEvents([]);
    setCursor(null);
    setIndex(0);
    abortExistingRequest();
    const res = await eventGetterFn({ ...filter, skipped_event_ids: [] }, fleet, abortSignal);
    setEvents(res?.events ?? []);
    setCursor(res?.cursor ?? null);

    setLoading(false);
  };

  const loadMore = async () => {
    setLoading(true);
    abortExistingRequest();

    // When using grouped review feature, when we're loading more events, we want to skip all the events
    // that are already in the queue, so we adjust the filter to include them in skipped_event_ids.
    const adjustedFilter = deepCopy(filter);

    const eventIdsOfAllEventsInTheQueue = new Set(events.map((e) => e.id));
    events.forEach((e) => {
      e.sub_events?.forEach((se) => {
        eventIdsOfAllEventsInTheQueue.add(se.id);
      });
    });

    adjustedFilter.skipped_event_ids = [...(filter.skipped_event_ids ?? []), ...eventIdsOfAllEventsInTheQueue];

    const res = await eventGetterFn({ ...adjustedFilter, cursor }, fleet, abortSignal);
    const newEventsOnly = (res?.events ?? []).filter((e) => !eventIdsOfAllEventsInTheQueue.has(e.id));
    setEvents([...events, ...newEventsOnly]);
    if (res) {
      setCursor(res.cursor);
    }
    setLoading(false);
  };

  const gotoNext = async (size = 1) => {
    if (index + size >= events.length - 1) {
      await loadMore();
    }
    setIndex(index + size);
  };

  const updateEventInQueue = (newData: Partial<Event>) => {
    setEvents((currentEventsState) => {
      const eventId = newData.id;
      const eventToUpdateIndex = currentEventsState.findIndex((e) => e.id === eventId);

      // update all parent events
      const updatedEvents = currentEventsState.map((e, i) =>
        deepCopy(i === eventToUpdateIndex ? { ...e, ...newData } : e),
      );

      // update all sub-events across all parent events
      updatedEvents.forEach((ue) => {
        ue.sub_events?.forEach((se, i) => {
          if (se.id === eventId) {
            ue.sub_events[i] = deepCopy({ ...se, ...newData });
          }
        });
      });

      return updatedEvents;
    });
  };

  const changeEventType = (fields: Partial<EventUpdate>) => {
    const copiedEvent = deepCopy(current);
    const { event_type_id, event_type_identifier } = fields;
    const newEventType = eventTypes?.find((et) => et.identifier === event_type_identifier);

    const eventToChange = {
      ...copiedEvent,
      event_type_identifier,
      event_type_marker_color_hex: newEventType?.marker_color_hex,
      event_type_name: newEventType?.name,
    };

    if (group_events) {
      // For grouped events, we update the event type on the sub-events
      current.sub_events?.forEach((subEvent) => {
        EventsService.updateEvent(subEvent.id, { event_type_id }).then(() =>
          updateEventInQueue({ ...eventToChange, id: subEvent.id }),
        );
      });
    } else {
      // For non-grouped events, we update the event type directly on the parent
      EventsService.updateEvent(eventToChange.id, { event_type_id }).then(() => updateEventInQueue(eventToChange));
    }
  };

  const externalEventUpdateHandler = (e: any) => {
    const { detail } = e;
    updateEventInQueue(detail);
  };

  useEffect(() => {
    resetQueue();
  }, [hash(filterWithoutCursor), fleet.id]);

  useEffect(() => {
    window.addEventListener('event-updated', externalEventUpdateHandler, false);

    return () => {
      window.removeEventListener('event-updated', externalEventUpdateHandler, false);
    };
  }, [events, updateEventInQueue]);

  return { events, isLoading, current, next, index, gotoNext, updateEventInQueue, changeEventType, resetQueue, prev };
};

const getMyAssignedEvents = async (filter: EventsFilter, fleet: Fleet, abortSignal?: AbortSignal) => {
  let alreadyAssignedEvents = await EventsService.listGroupedEvents(abortSignal);

  if (alreadyAssignedEvents?.events.length < filter.limit) {
    alreadyAssignedEvents = await EventsService.makeGroupedEvents(filter, fleet, abortSignal);
  }

  if (alreadyAssignedEvents?.expires_at) {
    setExpirationDateForGroups(alreadyAssignedEvents.expires_at);
  }

  return alreadyAssignedEvents;
};

export const useEventsQueue = (props: Props) => {
  const isGrouping = props.filter.group_events;
  const isConcurrentReview = useHasPermission('events:write:assignments');

  if (isGrouping) {
    if (isConcurrentReview) {
      return useGenericEventsQueue(props, getMyAssignedEvents);
    }
    return useGenericEventsQueue(props, EventsService.getGroupedEvents);
  }
  return useGenericEventsQueue(props, EventsService.getEvents);
};
